/**
 * WaitlistPage
 *
 * Admin page for managing waitlist signups and invitations.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardList,
  Search,
  Loader2,
  RefreshCw,
  Mail,
  Clock,
  XCircle,
  CheckCircle,
  UserPlus,
  Ban,
} from 'lucide-react';
import {
  useGetWaitlist,
  useUpdateWaitlistEntry,
  useInviteBetaUsers,
  WaitlistEntry as ApiWaitlistEntry,
  WaitlistStatus,
} from '../hooks/useAdminApi';

interface DisplayWaitlistEntry {
  id: string;
  email: string;
  name: string;
  signupDate: string;
  status: WaitlistStatus;
  source?: string;
}

type FilterTab = 'all' | 'pending' | 'invited' | 'declined' | 'expired';

/**
 * Convert Firestore timestamp to formatted date string
 */
function formatTimestamp(timestamp: { _seconds: number; _nanoseconds: number }): string {
  if (!timestamp || typeof timestamp._seconds !== 'number') {
    return '—';
  }
  const date = new Date(timestamp._seconds * 1000);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Transform API entry to display entry
 */
function transformEntry(entry: ApiWaitlistEntry): DisplayWaitlistEntry {
  return {
    id: entry.entryId,
    email: entry.email,
    name: entry.name,
    signupDate: formatTimestamp(entry.createdAt),
    status: entry.status,
    source: entry.source,
  };
}

export function WaitlistPage(): JSX.Element {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { execute, loading, error, data } = useGetWaitlist();
  const { mutate: updateWaitlistEntry, loading: updatingEntry } = useUpdateWaitlistEntry();
  const { mutate: inviteBetaUsers, loading: inviting } = useInviteBetaUsers();

  // Track which entry is being processed
  const [processingEntryId, setProcessingEntryId] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Fetch waitlist data on mount and when tab changes
  const fetchWaitlist = useCallback(async () => {
    try {
      const statusFilter = activeTab === 'all' ? undefined : activeTab;
      await execute({ status: statusFilter as WaitlistStatus, limit: 100 });
    } catch (err) {
      console.error('Failed to fetch waitlist:', err);
    }
  }, [execute, activeTab]);

  useEffect(() => {
    fetchWaitlist();
  }, [fetchWaitlist]);

  // Clear action result after 5 seconds
  useEffect(() => {
    if (actionResult) {
      const timer = setTimeout(() => setActionResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionResult]);

  /**
   * Approve a waitlist entry - invites user to beta and updates waitlist status
   */
  const handleApprove = async (entryId: string, email: string, name: string) => {
    setProcessingEntryId(entryId);
    setActionResult(null);

    try {
      // First, invite the user to beta
      const inviteResult = await inviteBetaUsers({ emails: [email] });

      if (inviteResult.invited.length > 0 || inviteResult.alreadyInvited.length > 0) {
        // Update waitlist entry status to 'invited'
        await updateWaitlistEntry({ entryId, status: 'invited' });

        setActionResult({
          type: 'success',
          message: `${name} has been approved for beta access`,
        });

        // Refresh the list
        await fetchWaitlist();
      } else if (inviteResult.invalid.length > 0) {
        setActionResult({
          type: 'error',
          message: `Invalid email address: ${email}`,
        });
      } else {
        setActionResult({
          type: 'error',
          message: 'Failed to approve user',
        });
      }
    } catch (err) {
      console.error('Failed to approve entry:', err);
      setActionResult({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to approve user',
      });
    } finally {
      setProcessingEntryId(null);
    }
  };

  /**
   * Decline a waitlist entry
   */
  const handleDecline = async (entryId: string, name: string) => {
    setProcessingEntryId(entryId);
    setActionResult(null);

    try {
      await updateWaitlistEntry({ entryId, status: 'declined' });

      setActionResult({
        type: 'success',
        message: `${name} has been declined`,
      });

      // Refresh the list
      await fetchWaitlist();
    } catch (err) {
      console.error('Failed to decline entry:', err);
      setActionResult({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to decline user',
      });
    } finally {
      setProcessingEntryId(null);
    }
  };

  const isProcessing = updatingEntry || inviting;

  // Transform API entries to display format
  const entries: DisplayWaitlistEntry[] = data?.entries?.map(transformEntry) ?? [];

  // Filter entries by search query (tab filtering is done via API)
  const filteredEntries = entries.filter((entry) => {
    const matchesSearch =
      !searchQuery ||
      entry.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entry.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // Use counts from API response
  const counts = {
    all: data?.totalCount ?? 0,
    pending: data?.counts?.pending ?? 0,
    invited: data?.counts?.invited ?? 0,
    declined: data?.counts?.declined ?? 0,
    expired: data?.counts?.expired ?? 0,
  };

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'pending', label: 'Pending', count: counts.pending },
    { key: 'invited', label: 'Invited', count: counts.invited },
    { key: 'declined', label: 'Declined', count: counts.declined },
    { key: 'expired', label: 'Expired', count: counts.expired },
  ];

  const statusColors: Record<WaitlistStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    invited: 'bg-blue-100 text-blue-700',
    declined: 'bg-red-100 text-red-700',
    expired: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="min-h-screen bg-airy">
      {/* Header */}
      <header className="bg-white border-b border-soft-neutral sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="btn-ghost btn-sm flex items-center gap-1"
              >
                <ArrowLeft size={16} />
                Admin
              </button>
              <div className="h-6 w-px bg-soft-neutral" />
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-evergreen" />
                <h1 className="text-xl font-semibold text-ink">Waitlist</h1>
              </div>
            </div>
            <button
              onClick={fetchWaitlist}
              disabled={loading}
              className="btn-ghost btn-sm flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <ClipboardList className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-ink">{counts.all}</p>
              <p className="text-xs text-muted">Total Signups</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-yellow-600">{counts.pending}</p>
              <p className="text-xs text-muted">Pending</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-blue-600">{counts.invited}</p>
              <p className="text-xs text-muted">Invited</p>
            </div>
          </div>
          <div className="card p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-red-600">{counts.declined}</p>
              <p className="text-xs text-muted">Declined</p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 transition-colors ${
                activeTab === tab.key
                  ? 'bg-evergreen text-white'
                  : 'bg-soft-neutral text-ink hover:bg-slate-200'
              }`}
            >
              {tab.label}
              <span
                className={`text-xs px-1.5 py-0.5 rounded ${
                  activeTab === tab.key ? 'bg-white/20' : 'bg-slate-300'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full md:w-96"
          />
        </div>

        {/* Action Result Toast */}
        {actionResult && (
          <div
            className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
              actionResult.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {actionResult.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span className="text-sm font-medium">{actionResult.message}</span>
            <button
              onClick={() => setActionResult(null)}
              className="ml-2 hover:opacity-70"
            >
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              <p className="font-medium">Failed to load waitlist</p>
            </div>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <button
              onClick={fetchWaitlist}
              className="mt-2 text-sm text-red-700 underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted" />
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredEntries.length === 0 && (
          <div className="text-center py-12">
            <ClipboardList className="w-12 h-12 mx-auto text-muted mb-4" />
            <p className="text-muted">
              {entries.length === 0
                ? 'No waitlist signups yet'
                : 'No entries match your filters'}
            </p>
          </div>
        )}

        {/* Entries Table */}
        {filteredEntries.length > 0 && (
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-soft-neutral/50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-muted">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-muted">Signup Date</th>
                  <th className="text-left px-4 py-3 font-medium text-muted">Source</th>
                  <th className="text-left px-4 py-3 font-medium text-muted">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-muted">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-soft-neutral">
                {filteredEntries.map((entry) => {
                  const isEntryProcessing = processingEntryId === entry.id;
                  const isPending = entry.status === 'pending';

                  return (
                    <tr key={entry.id} className="hover:bg-soft-neutral/30 transition-colors">
                      <td className="px-4 py-3 text-ink">{entry.name}</td>
                      <td className="px-4 py-3 text-muted">{entry.email}</td>
                      <td className="px-4 py-3 text-muted">{entry.signupDate}</td>
                      <td className="px-4 py-3 text-muted">{entry.source || '—'}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded ${statusColors[entry.status]}`}
                        >
                          {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {isPending ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleApprove(entry.id, entry.email, entry.name)}
                              disabled={isProcessing}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Approve for beta access"
                            >
                              {isEntryProcessing && inviting ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <UserPlus className="w-3 h-3" />
                              )}
                              Approve
                            </button>
                            <button
                              onClick={() => handleDecline(entry.id, entry.name)}
                              disabled={isProcessing}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              title="Decline waitlist entry"
                            >
                              {isEntryProcessing && updatingEntry ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Ban className="w-3 h-3" />
                              )}
                              Decline
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

export default WaitlistPage;
